import React, { useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { recruitingAPI } from "../services/recruitingApi";
import JobActivityTimeline from "../components/JobActivityTimeline";
import {
  JOB_ACTIVITY_TYPES,
  JOB_ACTIVITY_CONFIG,
  JobActivityItem,
  JobActivityHistoryResponse,
} from "../constants/jobActivityConfig";
import {
  ArrowLeft,
  Filter,
  Calendar,
  X,
  History as HistoryIcon,
} from "lucide-react";
import "./JobActivityHistory.css";

interface Job {
  id: number;
  title: string;
}

interface JobResponse {
  success: boolean;
  data?: Job;
}

interface ActivityHistoryOptions {
  limit: number;
  offset: number;
  eventTypes: string[] | null;
  startDate: string | null;
  endDate: string | null;
}

const JobActivityHistory: React.FC = () => {
  const { t } = useTranslation(["recruiting", "common"]);
  const { id } = useParams<{ id: string }>();
  const jobId = Number.parseInt(id || "0");

  const [options, setOptions] = useState<ActivityHistoryOptions>({
    limit: 50,
    offset: 0,
    eventTypes: null,
    startDate: null,
    endDate: null,
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: "",
    end: "",
  });
  const [allActivities, setAllActivities] = useState<JobActivityItem[]>([]);

  // Fetch job details for breadcrumb
  const { data: jobData } = useQuery<JobResponse>(
    ["job", jobId],
    () => recruitingAPI.getJob(jobId) as unknown as Promise<JobResponse>,
    { enabled: !!jobId },
  );

  // Fetch activity history
  const {
    data: historyData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    ["job-activity-history", jobId, options],
    async () => {
      const response = (await recruitingAPI.getJobActivityHistory(jobId, {
        limit: options.limit,
        offset: options.offset,
        eventTypes: options.eventTypes?.length ? options.eventTypes : undefined,
        startDate: options.startDate || undefined,
        endDate: options.endDate || undefined,
      })) as JobActivityHistoryResponse;
      return response;
    },
    {
      enabled: !!jobId,
      keepPreviousData: true,
      onSuccess: (data) => {
        if (options.offset === 0) {
          setAllActivities(data.data.activities);
        } else {
          setAllActivities((prev) => [...prev, ...data.data.activities]);
        }
      },
    },
  );

  const handleLoadMore = useCallback(() => {
    setOptions((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAllActivities([]);
    setOptions((prev) => ({
      ...prev,
      offset: 0,
      eventTypes: selectedTypes.size > 0 ? Array.from(selectedTypes) : null,
      startDate: dateRange.start || null,
      endDate: dateRange.end || null,
    }));
    setShowFilters(false);
  }, [selectedTypes, dateRange]);

  const handleClearFilters = useCallback(() => {
    setSelectedTypes(new Set());
    setDateRange({ start: "", end: "" });
    setAllActivities([]);
    setOptions({
      limit: 50,
      offset: 0,
      eventTypes: null,
      startDate: null,
      endDate: null,
    });
    setShowFilters(false);
  }, []);

  const toggleEventType = (type: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const hasActiveFilters =
    options.eventTypes !== null ||
    options.startDate !== null ||
    options.endDate !== null;

  const job = jobData?.data;

  return (
    <div className="job-activity-history-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-breadcrumb">
          <Link to={`/jobs/${jobId}`} className="back-link">
            <ArrowLeft size={18} />
            {t("jobActivity.backToJob")}
          </Link>
          {job && <span className="job-title-breadcrumb">{job.title}</span>}
        </div>
        <h1>
          <HistoryIcon size={24} />
          {t("jobActivity.title")}
        </h1>
        {historyData?.data && (
          <p className="total-count">
            {t("jobActivity.totalActivities", {
              count: historyData.data.total,
            })}
          </p>
        )}
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <button
          className={`filter-toggle ${showFilters ? "active" : ""}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={16} />
          {t("jobActivity.filters")}
          {hasActiveFilters && <span className="filter-badge"></span>}
        </button>

        {hasActiveFilters && (
          <button className="clear-filters" onClick={handleClearFilters}>
            <X size={14} />
            {t("common:actions.clearAll")}
          </button>
        )}
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="filter-panel">
          <div className="filter-section">
            <h3>{t("jobActivity.activityType")}</h3>
            <div className="event-type-filters">
              {Object.entries(JOB_ACTIVITY_TYPES).map(([key, value]) => {
                const config = JOB_ACTIVITY_CONFIG[value];
                return (
                  <label key={key} className="event-type-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedTypes.has(value)}
                      onChange={() => toggleEventType(value)}
                    />
                    <span
                      className="checkbox-label"
                      style={{ borderColor: config.color }}
                    >
                      {config.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="filter-section">
            <h3>
              <Calendar size={16} />
              {t("jobActivity.dateRange")}
            </h3>
            <div className="date-range-inputs">
              <div className="date-input-group">
                <label>{t("jobActivity.from")}</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, start: e.target.value }))
                  }
                />
              </div>
              <div className="date-input-group">
                <label>{t("jobActivity.to")}</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, end: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="filter-actions">
            <button
              className="btn-secondary"
              onClick={() => setShowFilters(false)}
            >
              {t("common:actions.cancel")}
            </button>
            <button className="btn-primary" onClick={handleApplyFilters}>
              {t("jobActivity.applyFilters")}
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="timeline-container">
        <JobActivityTimeline
          activities={allActivities}
          isLoading={isLoading && options.offset === 0}
          variant="full"
          onLoadMore={handleLoadMore}
          hasMore={historyData?.data?.hasMore || false}
          loadingMore={isFetching && options.offset > 0}
        />
      </div>
    </div>
  );
};

export default JobActivityHistory;
